<template>
  <div class="profile-panel sidebar-content__panel">
    <div class="profile-panel-header">
      <h2>My Profile</h2>

      <button @click="close" aria-label="Close" class="profile-panel-header__close">
        <img aria-hidden="true" src="@/assets/global-icons/cross-white-circle.svg" />
      </button>
    </div>

    <div class="user-container">
      <div class="image-container">
        <btn aria-label="Delete image" circular colour="black" @click="resetFileOrDelete" :disabled="!currentUsersAvatar">
          <template #image>
            <img src="~@/assets/notification-icons/clear-notifications.svg" aria-hidden="true" />
          </template>
        </btn>

        <div class="user-image">
          <UserIcon class="user-image-UserIcon" :currentUser="true" />
          <img class="user-image__temporary" v-if="activeUrl" :src="activeUrl" />
        </div>

        <btn aria-label="Upload image" @click="$refs.fileInput.click()" circular colour="black">
          <template #image>
            <img src="~@/assets/global-icons/upload-camera.svg" aria-hidden="true" />
          </template>
        </btn>

        <input class="value" type="file" style="display: none" accept="image/*" @change="fileChanged" ref="fileInput" />
      </div>

      <div class="user-details-container">
        <TextInput inset label="Name" v-model="firstName" />
        <TextInput inset label="Surname" v-model="lastName" />
        <Select label="Pronouns" :options="pronounOptions" v-model="pronouns" />

        <template v-if="pronouns.value == 'custom'">
          <TextInput inset label="Enter custom pronouns" v-model="customPronouns" />
          <div class="pronoun-warning">
            <img src="@/assets/global-icons/info.svg" aria-hidden="true" class="pronoun-warning__icon" />
            <div class="pronoun-warning__text">
              This is intended for SPACEIN users to identify their pronouns. We ask you not use this field to make a joke.
            </div>
          </div>
        </template>

        <TextInput
          inset
          readonly
          class="gap"
          label="Email"
          v-model="email"
          @onFocus="showEmailPrompt = true"
          @onBlur="showEmailPrompt = false"
        />

        <FadeTransition>
          <div v-if="showEmailPrompt" class="email-prompt">
            Please email <a href="mailto:support@spacein.co.uk">support@spacein.co.uk</a> if you wish to change your SPACEIN
            email address.
          </div>
        </FadeTransition>
        <TextInput inset label="Optional Field One" v-model="field1" />
        <TextInput inset label="Optional Field Two" v-model="field2" />
        <TextInput :error="linkedInError" class="gap" inset label="Add LinkedIn Profile URL" v-model="linkedInProfileUrl" />
      </div>
    </div>

    <footer class="footer">
        <btn :disabled="!dirty || !isValid || submittedChanges" class="footer__update" @click="submit" colour="green">
          <template #text>Save changes</template>
        </btn>
        <small class="footer__delete-account">Please email <a href="mailto:support@spacein.co.uk">support@spacein.co.uk</a> if you wish to delete your SPACEIN account</small>
    </footer>
  </div>
</template>

<script>
import UserIcon from '../UserIcon';
import { convertToJPG } from '@/functions/utility.js';
import { mapActions, mapGetters } from 'vuex';
import { TextInput, Select } from '@/framework';
import FormChangeWatcherMixin from '@/plugins/mixins/formChangeWatcher.js';
import FadeTransition from '@/components/transitions/FadeTransition.vue';

export default {
  name: 'ProfilePanel',
  components: {
    UserIcon,
    TextInput,
    Select,
    FadeTransition
  },
  props: {
    data: Object
  },
  mixins: [FormChangeWatcherMixin],
  data: ({
    $store: {
      getters: { 'current_user/data': user }
    }
  }) => ({
    id: user.id,
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    email: user.email || '',
    field1: user.field1 || '',
    field2: user.field2 || '',
    customPronouns: user.pronouns,
    linkedInProfileUrl: user.linkedInProfileUrl || '',
    pronouns: {}, // This will be set in mounted()
    pronounOptions: [
      {
        label: 'Please select',
        value: ''
      },
      {
        label: 'She/Her',
        value: 'She/Her'
      },
      {
        label: 'He/Him',
        value: 'He/Him'
      },
      {
        label: 'They/Them',
        value: 'They/Them'
      },
      {
        label: 'Custom',
        value: 'custom'
      }
    ],
    activeUrl: '',
    activeFile: null,

    formChangeWatcherSettings: {
      // This tells FormChangeWatcherMixin to watch these inputs
      // so we know when to present the "Save" button
      inputs: [
        "firstName",
        "lastName",
        "pronounsData",
        "field1",
        "field2",
        "activeUrl",
        "linkedInProfileUrl"
      ],

      defaults: {
        firstName: '',
        lastName: '',
        pronounsData: '',
        field1: '',
        field2: '',
        activeUrl: '',
        linkedInProfileUrl: '',
      },

      promptOnLeave: false
    },

    showEmailPrompt: false,
    submittedChanges: false,
  }),
  mounted() {
    this.ignoreChanges(() => {
      this.pronouns = this.getInitialPronouns;
    });
  },
  computed: {
    ...mapGetters({
      getCurrentUsersData: 'current_user/data',
      currentUsersAvatar: 'current_user/avatar',
    }),
    getInitialPronouns() {
      const user = this.getCurrentUsersData;
      // No pronouns set? Leave at "Please select"
      if (!user.pronouns || user.pronouns == '') {
        return this.pronounOptions[0]; // Please select
      }

      // Otherwise find the matching option
      const foundPronouns = this.pronounOptions.find((p) => p.value == user.pronouns);

      if (foundPronouns) return foundPronouns;

      // If no matching option, assume custom
      return this.pronounOptions.find((p) => p.value == 'custom');
    },
    pronounsData() {
      if (this.pronouns.value == 'custom') {
        return this.customPronouns;
      } else {
        return this.pronouns.value;
      }
    },
    isValid() {
      return this.firstName && this.lastName && this.email && !this.linkedInError
    },
    linkedInError() {
      if (this.linkedInProfileUrl) {
        const url = this.linkedInProfileUrl.trim()
        if (!url.startsWith('https://www.linkedin.com/in/')) {
          return 'Please enter a valid LinkedIn profile URL'
        }
      }
      return ''
    }
  },
  methods: {
    ...mapActions([
      'modal/close',
      'sidebar/open',
      'current_user/updateUserHandler',
      'current_user/confirmDeleteAvatar',
    ]),
    resetFileOrDelete() {
      if(this.activeUrl) { //clear uploaded file
        URL.revokeObjectURL(this.activeUrl)
        this.ignoreChanges(() => {
          this.activeUrl = ''
          this.activeFile = null
        })
      } else { //delete avatar
        this['current_user/confirmDeleteAvatar']()
      }
    },
    close() {
      this.promptIfDirty(() => this['sidebar/open']('settings'))
    },
    async fileChanged(e) {
      // we could preview the file directly, however, if the use tries an animated GIF, or a transparent PNG...
      // ...those will look/act different after being uploaded to and processed by the server, because the server converts them all to JPG
      // so we do the conversion to JPG now, so the user will see more or less the exact same thing before/after upload
      this.activeFile = await convertToJPG(e.target.files[0])
      this.activeUrl = URL.createObjectURL(this.activeFile)
      // this function is only called when the file changes, which means if the user selects the same file twice, it doesn't work
      // unless we clear the input after capturing it
      e.target.value = ''
    },
    async submit() {
      if(this.submittedChanges) return
      this.submittedChanges = true
      let [dataRes, uploadRes] = await this['current_user/updateUserHandler']([this.activeFile, [this.firstName, this.lastName, this.pronounsData, this.field1, this.field2, this.linkedInProfileUrl]])
      this.submittedChanges = false

      if (dataRes.success) {
        this.resetDirtyInputs()
        if (!uploadRes) return this['modal/close']()
      }
      if (uploadRes.success) this.resetFileOrDelete()
    }
  }
};
</script>

<style lang="scss" scoped>
.profile-panel {
  color: white;
  padding: 20px;

  &-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    h2 {
      font-size: 28px;
    }

    &__close {
      background: none;
      color: inherit;
      border: none;
      padding: 0;
      font: inherit;
      cursor: pointer;
      outline: inherit;
    }

    margin-bottom: 50px;
  }
}

.user-container {
  display: flex;
  gap: 1em;
  margin: 10px 0;
  flex-direction: column;
}

.pronoun-warning {
  display: flex;
  padding: 5px;
  gap: 10px;
  margin-bottom: 20px;
  margin-top: 5px;

  &__icon {
    width: 20px;
    height: 20px;
    margin-top: 5px;
  }

  &__text {
    font-size: 0.75rem; /** 12px */
    line-height: 1.5;
    color: $grey-2;
  }
}

.gap {
  margin-top: 20px;
}

.email-prompt {
  font-size: 0.75rem; /** 12px */
  line-height: 1.5;
  color: $grey-2;
  padding: 5px;
  margin-bottom: 20px;
  margin-top: 5px;

  a {
    color: white;

    &:hover {
      color: $grey-2;
    }
  }
}

.footer {
  padding: 5px;

  &__update {
    margin-left: auto;
    margin-bottom: 40px;

    @include mobile-only {
      width: 100%;
    }
  }

  &__delete-account {
    display: block;
    color: $red;
    font-size: 0.75rem; /** 12px */

    a {
      color: $red;

      &:hover {
        color: #ff616c;
      }
    }

    margin-bottom: 10px;
  }
}
.image-container {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 15px;

  .user-image {
    position: relative;
    height: 140px;
    width: 140px;

    &__temporary {
      /** This image is essentially plopped on top of the UserIcon */
      /** and shifted slightly to fit inside the 3px border */
      position: absolute;
      top: 2.5px;
      left: 2.5px;
      width: calc(100% - 5px);
      height: calc(100% - 5px);
      border-radius: 50%;
      object-fit: cover;
    }
  }

  .user-icon {
    box-sizing: border-box;
    height: 140px;
    width: 140px;
  }
}

.user-details-container {
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  gap: 5px;
}
</style>
